import { useCallback, useEffect, useState } from "react";
import {
  ConditionType,
  DayOfWeek,
  DeviceType,
  Hemisphere,
  OrchestrationCondition,
  SeasonType,
  getConditionType,
} from "../domain/orchestrationCriteria.entity";
import { parseCommaSeparated } from "../infrastructure/globalActionsMapper";
import {
  DEFAULT_TIMEZONE,
  WindowFormValue,
  dateToTimeString,
  emptyWindow,
  timeStringToDate,
} from "../utils/criteriaWindow.helpers";

export interface OccupancyTypeFilters {
  adults: boolean;
  children: boolean;
  juniors: boolean;
  seniors: boolean;
  babies: boolean;
}

const emptyOccupancyFilters = (): OccupancyTypeFilters => ({
  adults: false,
  children: false,
  juniors: false,
  seniors: false,
  babies: false,
});

const WINDOW_TYPES = [
  ConditionType.BOOKING_WINDOW,
  ConditionType.TRAVEL_WINDOW,
  ConditionType.CHECK_IN_DAY,
  ConditionType.CHECK_OUT_DAY,
];

interface UseCriteriaFormArgs {
  isOpen: boolean;
  editingCondition?: OrchestrationCondition;
  onSave: (condition: OrchestrationCondition) => void;
}

export function useCriteriaForm({ isOpen, editingCondition, onSave }: UseCriteriaFormArgs) {
  const [selectedType, setSelectedType] = useState<ConditionType | "">("");
  const [stringListValue, setStringListValue] = useState("");
  const [windows, setWindows] = useState<WindowFormValue[]>([]);
  const [minValue, setMinValue] = useState<number>(1);
  const [maxValue, setMaxValue] = useState<string>("");
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [seasons, setSeasons] = useState<SeasonType[]>([]);
  const [hemisphere, setHemisphere] = useState<Hemisphere>(Hemisphere.NORTHERN);
  const [selectedOccupations, setSelectedOccupations] = useState<string[]>([]);
  const [occupancyTypeFilters, setOccupancyTypeFilters] = useState<OccupancyTypeFilters>(
    emptyOccupancyFilters()
  );

  const resetForm = useCallback(() => {
    setSelectedType("");
    setStringListValue("");
    setWindows([]);
    setMinValue(1);
    setMaxValue("");
    setDeviceTypes([]);
    setSeasons([]);
    setHemisphere(Hemisphere.NORTHERN);
    setSelectedOccupations([]);
    setOccupancyTypeFilters(emptyOccupancyFilters());
  }, []);

  const populateFormFromCondition = useCallback(
    (condition: OrchestrationCondition, type: ConditionType | null) => {
      if (!type) return;

      switch (type) {
        case ConditionType.OCCUPANCY:
          setSelectedOccupations(condition.occupancy?.values || []);
          break;
        case ConditionType.CHECK_IN_DAY:
          setWindows(
            (condition.checkInDay?.activeDays || []).length > 0
              ? [
                  {
                    ...emptyWindow(),
                    activeDays: (condition.checkInDay?.activeDays as DayOfWeek[]) || [],
                  },
                ]
              : [emptyWindow()]
          );
          break;
        case ConditionType.CHECK_OUT_DAY:
          setWindows(
            (condition.checkOutDay?.activeDays || []).length > 0
              ? [
                  {
                    ...emptyWindow(),
                    activeDays: (condition.checkOutDay?.activeDays as DayOfWeek[]) || [],
                  },
                ]
              : [emptyWindow()]
          );
          break;
        case ConditionType.LENGTH_OF_STAY:
          setMinValue(condition.lengthOfStay?.min || 1);
          setMaxValue(
            condition.lengthOfStay?.max !== undefined && condition.lengthOfStay?.max !== null
              ? String(condition.lengthOfStay.max)
              : ""
          );
          break;
        case ConditionType.BOOKING_WINDOW:
          setWindows(
            (condition.bookingWindow || []).map(w => ({
              ...emptyWindow(),
              startDate: w.startDate || "",
              endDate: w.endDate || "",
              activeDays: (w.activeDays as DayOfWeek[]) || [],
              startHour: timeStringToDate(w.startHour),
              endHour: timeStringToDate(w.endHour),
              timeZone: w.timeZone || DEFAULT_TIMEZONE,
            }))
          );
          break;
        case ConditionType.TRAVEL_WINDOW:
          setWindows(
            (condition.travelWindow || []).map(w => ({
              ...emptyWindow(),
              startDate: w.startDate || "",
              endDate: w.endDate || "",
              activeDays: (w.activeDays as DayOfWeek[]) || [],
            }))
          );
          break;
        case ConditionType.NUMBER_OF_ROOMS:
          setMinValue(condition.numberOfRooms?.min || 1);
          setMaxValue(
            condition.numberOfRooms?.max !== undefined && condition.numberOfRooms?.max !== null
              ? String(condition.numberOfRooms.max)
              : ""
          );
          break;
        case ConditionType.DEVICE:
          setDeviceTypes((condition.device?.types as DeviceType[]) || []);
          break;
        case ConditionType.REFERRER:
          setStringListValue(condition.referrer?.values?.join(", ") || "");
          break;
        case ConditionType.UTM_SOURCE:
          setStringListValue(condition.utmSource?.values?.join(", ") || "");
          break;
        case ConditionType.ORIGIN:
          setStringListValue(condition.origin?.values?.join(", ") || "");
          break;
        case ConditionType.SEASON:
          setSeasons((condition.season?.seasons as SeasonType[]) || []);
          setHemisphere(condition.season?.hemisphere || Hemisphere.NORTHERN);
          break;
      }
    },
    []
  );

  useEffect(() => {
    if (isOpen) {
      if (editingCondition) {
        const type = getConditionType(editingCondition);
        setSelectedType(type || "");
        populateFormFromCondition(editingCondition, type);
      } else {
        resetForm();
      }
    }
  }, [isOpen, editingCondition, populateFormFromCondition, resetForm]);

  const handleTypeChange = useCallback((type: ConditionType | "") => {
    setSelectedType(type);
    setStringListValue("");
    setMinValue(1);
    setMaxValue("");
    setDeviceTypes([]);
    setSeasons([]);
    setHemisphere(Hemisphere.NORTHERN);
    setSelectedOccupations([]);
    setOccupancyTypeFilters(emptyOccupancyFilters());
    setWindows(type && WINDOW_TYPES.includes(type as ConditionType) ? [emptyWindow()] : []);
  }, []);

  const handleDayToggle = useCallback((windowId: string, day: DayOfWeek) => {
    setWindows(prev =>
      prev.map(w => {
        if (w.id !== windowId) return w;
        return {
          ...w,
          activeDays: w.activeDays.includes(day)
            ? w.activeDays.filter(d => d !== day)
            : [...w.activeDays, day],
        };
      })
    );
  }, []);

  const handleDeviceToggle = useCallback((device: DeviceType) => {
    setDeviceTypes(prev =>
      prev.includes(device) ? prev.filter(d => d !== device) : [...prev, device]
    );
  }, []);

  const handleSeasonToggle = useCallback((season: SeasonType) => {
    setSeasons(prev =>
      prev.includes(season) ? prev.filter(s => s !== season) : [...prev, season]
    );
  }, []);

  const handleSave = useCallback(() => {
    if (!selectedType) return;

    const condition: OrchestrationCondition = {};

    switch (selectedType) {
      case ConditionType.OCCUPANCY:
        condition.occupancy = { values: selectedOccupations };
        break;
      case ConditionType.CHECK_IN_DAY:
        condition.checkInDay = {
          activeDays:
            windows.length > 0 && windows[0].activeDays.length > 0
              ? windows[0].activeDays
              : Object.values(DayOfWeek),
        };
        break;
      case ConditionType.CHECK_OUT_DAY:
        condition.checkOutDay = {
          activeDays:
            windows.length > 0 && windows[0].activeDays.length > 0
              ? windows[0].activeDays
              : Object.values(DayOfWeek),
        };
        break;
      case ConditionType.LENGTH_OF_STAY:
        condition.lengthOfStay = {
          min: minValue,
          max: maxValue ? parseInt(maxValue, 10) : undefined,
        };
        break;
      case ConditionType.BOOKING_WINDOW:
        condition.bookingWindow = windows.map(w => ({
          startDate: w.startDate || undefined,
          endDate: w.endDate || undefined,
          activeDays: w.activeDays.length > 0 ? w.activeDays : Object.values(DayOfWeek),
          startHour: dateToTimeString(w.startHour),
          endHour: dateToTimeString(w.endHour),
          timeZone: w.timeZone || undefined,
        }));
        break;
      case ConditionType.TRAVEL_WINDOW:
        condition.travelWindow = windows.map(w => ({
          startDate: w.startDate || undefined,
          endDate: w.endDate || undefined,
          activeDays: w.activeDays.length > 0 ? w.activeDays : Object.values(DayOfWeek),
        }));
        break;
      case ConditionType.NUMBER_OF_ROOMS:
        condition.numberOfRooms = {
          min: minValue,
          max: maxValue ? parseInt(maxValue, 10) : undefined,
        };
        break;
      case ConditionType.DEVICE:
        condition.device = { types: deviceTypes };
        break;
      case ConditionType.REFERRER:
        condition.referrer = { values: parseCommaSeparated(stringListValue) };
        break;
      case ConditionType.UTM_SOURCE:
        condition.utmSource = { values: parseCommaSeparated(stringListValue) };
        break;
      case ConditionType.ORIGIN:
        condition.origin = { values: parseCommaSeparated(stringListValue) };
        break;
      case ConditionType.SEASON:
        condition.season = { seasons, hemisphere };
        break;
    }

    onSave(condition);
  }, [
    selectedType,
    selectedOccupations,
    windows,
    minValue,
    maxValue,
    deviceTypes,
    seasons,
    hemisphere,
    stringListValue,
    onSave,
  ]);

  return {
    selectedType,
    stringListValue,
    setStringListValue,
    windows,
    setWindows,
    minValue,
    setMinValue,
    maxValue,
    setMaxValue,
    deviceTypes,
    seasons,
    hemisphere,
    setHemisphere,
    selectedOccupations,
    setSelectedOccupations,
    occupancyTypeFilters,
    setOccupancyTypeFilters,
    handleTypeChange,
    handleDayToggle,
    handleDeviceToggle,
    handleSeasonToggle,
    handleSave,
  };
}
